/* =========================================================
   FILE: cn-footer.js
   Cardcomplex Footer
   - Mobile Accordions
   - Vorteile-Slider Fades
   - Cookie-Einstellungen
   - Reflow-optimiert
========================================================= */

(() => {
  'use strict';

  const initialized = new WeakSet();

  const setClass = (element, className, enabled) => {
    if (element.classList.contains(className) === enabled) return;
    element.classList.toggle(className, enabled);
  };

  const initFooter = (footer) => {
    if (!footer || initialized.has(footer)) return;
    initialized.add(footer);

    /* =========================
       MOBILE ACCORDIONS
    ========================= */

    const accordions = Array.from(
      footer.querySelectorAll('[data-footer-accordion]')
    );
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    const closeOtherAccordions = (current) => {
      accordions.forEach((accordion) => {
        if (accordion !== current && accordion.hasAttribute('open')) {
          accordion.removeAttribute('open');
        }
      });
    };

    const setAccordionState = () => {
      const isMobile = mobileQuery.matches;

      accordions.forEach((accordion) => {
        if (!isMobile) {
          if (!accordion.hasAttribute('open')) {
            accordion.setAttribute('open', '');
          }
          return;
        }

        const shouldOpen = Boolean(
          accordion.querySelector('.cn-newsletter-form__message')
        );

        if (accordion.hasAttribute('open') !== shouldOpen) {
          accordion.toggleAttribute('open', shouldOpen);
        }
      });
    };

    accordions.forEach((accordion) => {
      accordion
        .querySelector('.cn-footer__accordion-summary')
        ?.addEventListener('click', (event) => {
          if (!mobileQuery.matches) {
            event.preventDefault();

            if (!accordion.hasAttribute('open')) {
              accordion.setAttribute('open', '');
            }
          }
        });

      accordion.addEventListener('toggle', () => {
        if (!mobileQuery.matches) {
          if (!accordion.hasAttribute('open')) {
            accordion.setAttribute('open', '');
          }
          return;
        }

        if (accordion.open) closeOtherAccordions(accordion);
      });
    });

    setAccordionState();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', setAccordionState);
    } else {
      mobileQuery.addListener(setAccordionState);
    }

    /* =========================
       PERKS SLIDER
       Reads und Writes werden getrennt gebündelt.
       Die Geometrie wird erst berechnet, wenn der Footer
       in die Nähe des Viewports kommt.
    ========================= */

    const sliders = Array.from(
      footer.querySelectorAll('.cn-footer__perks')
    );

    let sliderRaf = 0;
    let sliderGeometryActive = false;
    let resizeObserver = null;

    const updateSliderFades = () => {
      if (!sliderGeometryActive || !sliders.length) return;

      const threshold = 2;

      /*
        PHASE 1: ausschließlich Layout-Werte lesen.
        Dadurch verursacht das Schreiben der Klassen nicht vor dem
        nächsten Slider erneut eine synchrone Layout-Berechnung.
      */
      const states = sliders.map((slider) => {
        const maxScrollLeft = Math.max(
          0,
          slider.scrollWidth - slider.clientWidth
        );
        const scrollLeft = Math.max(0, slider.scrollLeft);
        const isScrollable = maxScrollLeft > threshold;

        return {
          slider,
          isScrollable,
          isAtStart: !isScrollable || scrollLeft <= threshold,
          isAtEnd:
            !isScrollable ||
            scrollLeft >= maxScrollLeft - threshold
        };
      });

      /*
        PHASE 2: ausschließlich DOM-Klassen schreiben.
      */
      states.forEach(
        ({ slider, isScrollable, isAtStart, isAtEnd }) => {
          setClass(
            slider,
            'cn-footer__perks--scrollable',
            isScrollable
          );
          setClass(
            slider,
            'cn-footer__perks--at-start',
            isAtStart
          );
          setClass(
            slider,
            'cn-footer__perks--at-end',
            isAtEnd
          );
        }
      );
    };

    const scheduleSliderUpdate = () => {
      if (!sliderGeometryActive || sliderRaf) return;

      sliderRaf = requestAnimationFrame(() => {
        sliderRaf = 0;
        updateSliderFades();
      });
    };

    const activateSliderGeometry = () => {
      if (sliderGeometryActive || !sliders.length) return;
      sliderGeometryActive = true;

      sliders.forEach((slider) => {
        slider.addEventListener(
          'scroll',
          scheduleSliderUpdate,
          { passive: true }
        );
      });

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(scheduleSliderUpdate);
        sliders.forEach((slider) => resizeObserver.observe(slider));
      } else {
        window.addEventListener(
          'resize',
          scheduleSliderUpdate,
          { passive: true }
        );
      }

      scheduleSliderUpdate();
    };

    if (sliders.length) {
      if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            observer.disconnect();
            activateSliderGeometry();
          },
          {
            rootMargin: '500px 0px'
          }
        );

        visibilityObserver.observe(footer);
      } else if ('requestIdleCallback' in window) {
        requestIdleCallback(activateSliderGeometry, { timeout: 1500 });
      } else {
        window.addEventListener(
          'load',
          activateSliderGeometry,
          { once: true }
        );
      }
    }

    /* =========================
       COOKIE SETTINGS
    ========================= */

    footer
      .querySelector('[data-cn-footer-cookie-settings]')
      ?.addEventListener('click', (event) => {
        event.preventDefault();

        if (window.privacyBanner?.showPreferences) {
          window.privacyBanner.showPreferences();
          return;
        }

        window.Shopify?.customerPrivacy?.showPreferences?.();
      });
  };

  const init = (scope = document) => {
    if (scope.matches?.('.cn-footer')) {
      initFooter(scope);
    }

    scope.querySelectorAll?.('.cn-footer').forEach(initFooter);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
